import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileText, Download, Trash2, Loader2, CheckCircle2, Circle, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';

const BRAND = '#500000';
const auth = () => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const eur = (v) => `€${Number(v || 0).toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('nl-BE') : '');
const pc = (v) => `${Number(v || 0).toLocaleString('nl-BE', { maximumFractionDigits: 2 })}%`;

export const ReminderToggle = ({ inv, onChanged }) => {
  const on = !!inv.auto_reminders;
  const toggle = async () => {
    try {
      await axios.put(`${API}/invoices/${inv.id}/reminders`, { enabled: !on }, { headers: auth() });
      toast.success(on ? 'Automatische herinneringen uitgezet' : 'Automatische herinneringen aangezet');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Wijzigen mislukt'); }
  };
  return (
    <button type="button" onClick={toggle} data-testid={`invoice-reminder-toggle-${inv.id}`}
      title="Wekelijkse betaalherinnering zodra de factuur vervallen is"
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${on ? 'border-[#500000] text-[#500000] bg-white hover:bg-[#f5e6e6]' : 'border-gray-300 text-gray-500 bg-gray-50 hover:bg-white'}`}>
      {on ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}{on ? 'Herinneringen aan' : 'Herinneringen uit'}
    </button>
  );
};

const InvoiceRow = ({ inv, onChanged }) => {
  const paid = inv.payment_status === 'paid';
  const due = inv.due_date ? new Date(inv.due_date) : null;
  const overdueDays = !paid && due ? Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(due).setHours(0, 0, 0, 0)) / 86400000) : 0;
  const download = async () => {
    try {
      const res = await axios.get(`${API}/invoices/${inv.id}/pdf`, { headers: auth(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('PDF kon niet geladen worden'); }
  };
  const togglePaid = async () => {
    if (paid && !window.confirm(`Factuur ${inv.invoice_number} terug op onbetaald zetten?`)) return;
    try {
      await axios.put(`${API}/invoices/${inv.id}`, paid ? { payment_status: 'unpaid' } : { payment_status: 'paid', paid_date: new Date().toISOString() }, { headers: auth() });
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Status wijzigen mislukt'); }
  };
  const remove = async () => {
    if (!window.confirm(`Factuur ${inv.invoice_number} verwijderen?`)) return;
    try { await axios.delete(`${API}/invoices/${inv.id}`, { headers: auth() }); toast.success('Factuur verwijderd'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Verwijderen mislukt'); }
  };
  return (
    <div data-testid={`quote-invoice-row-${inv.id}`} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-3 border-b last:border-b-0">
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm" style={{ color: '#1F2937' }}>{inv.invoice_number} · {inv.label || inv.milestone}</p>
        <p className="text-xs" style={{ color: '#6B7280' }}>{fmtDate(inv.invoice_date)} · {pc(inv.milestone_percentage)} van de offerte · vervalt {fmtDate(inv.due_date)}</p>
        {overdueDays > 0 && (
          <p className="text-xs font-medium text-red-700" data-testid={`quote-invoice-overdue-${inv.id}`}>
            {overdueDays} dag{overdueDays === 1 ? '' : 'en'} vervallen{inv.reminder_count ? ` · ${inv.reminder_count}× herinnerd, laatst ${fmtDate(inv.last_reminder_at)}` : inv.auto_reminders ? ' · herinnering volgt automatisch' : ''}
          </p>
        )}
        {!paid && <div className="mt-1"><ReminderToggle inv={inv} onChanged={onChanged} /></div>}
      </div>
      <span data-testid={`quote-invoice-amount-${inv.id}`} className="font-bold text-sm whitespace-nowrap">{eur(inv.total_incl_vat)}</span>
      <button type="button" onClick={togglePaid} data-testid={`quote-invoice-paid-toggle-${inv.id}`}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${paid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800 hover:bg-amber-200'}`}>
        {paid ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}{paid ? 'Betaald' : 'Onbetaald'}
      </button>
      <div className="flex gap-1">
        <Button size="sm" variant="outline" onClick={download} data-testid={`quote-invoice-pdf-${inv.id}`}><Download className="w-4 h-4" /></Button>
        {!paid && <Button size="sm" variant="outline" onClick={remove} data-testid={`quote-invoice-delete-${inv.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
      </div>
    </div>
  );
};

const NewInvoiceForm = ({ quoteId, summary, onCreated }) => {
  const [pct, setPct] = useState('');
  const [label, setLabel] = useState('');
  const [term, setTerm] = useState('14');
  const [busy, setBusy] = useState(false);
  const remaining = summary.remaining_percentage;
  const value = parseFloat(String(pct).replace(',', '.'));
  const valid = value > 0 && value <= remaining + 0.0001;
  const preview = valid ? (value + summary.invoiced_percentage >= 99.9999 ? summary.remaining_amount : summary.quote_total_incl_vat * value / 100) : 0;
  const submit = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/quotes/${quoteId}/invoices`, { percentage: value, label: label || null, payment_term_days: parseInt(term, 10) || 0 }, { headers: auth() });
      toast.success(`Factuur ${res.data.invoice_number} aangemaakt`);
      setPct(''); setLabel('');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Factuur maken mislukt'); }
    finally { setBusy(false); }
  };
  if (remaining <= 0) return <p data-testid="quote-invoice-fully-invoiced" className="text-sm font-medium text-green-700">Deze offerte is volledig (100%) gefactureerd.</p>;
  const quick = [...new Set([25, 50, remaining].filter((p) => p <= remaining))];
  return (
    <div className="rounded-lg border p-4 space-y-3" style={{ backgroundColor: '#FAF7F7' }}>
      <div className="flex flex-wrap gap-2">
        {quick.map((p) => (
          <button key={p} type="button" data-testid={`quote-invoice-quick-${p}`} onClick={() => setPct(String(p))}
            className="px-3 py-1 rounded-full text-xs font-semibold border transition-colors hover:bg-white" style={{ borderColor: BRAND, color: BRAND }}>
            {p === remaining ? `Rest (${pc(p)})` : pc(p)}
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <Label htmlFor="inv-pct">Percentage van offerte</Label>
          <Input id="inv-pct" data-testid="quote-invoice-percentage-input" inputMode="decimal" placeholder={`max ${pc(remaining)}`} value={pct} onChange={(e) => setPct(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="inv-label">Omschrijving (optioneel)</Label>
          <Input id="inv-label" data-testid="quote-invoice-label-input" placeholder="bv. Voorschot" value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="inv-term">Betaaltermijn (dagen)</Label>
          <Input id="inv-term" data-testid="quote-invoice-term-input" type="number" min="0" value={term} onChange={(e) => setTerm(e.target.value)} />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-sm" style={{ color: '#374151' }} data-testid="quote-invoice-preview">
          {valid ? <>Factuurbedrag: <strong>{eur(preview)}</strong> incl. BTW — alle posten × {pc(value)}</> : pct ? <span className="text-red-600">Kies een percentage tussen 0 en {pc(remaining)}</span> : 'Geef een percentage in'}
        </p>
        <Button data-testid="quote-invoice-create-btn" disabled={!valid || busy} onClick={submit} style={{ backgroundColor: BRAND }} className="text-white hover:opacity-90">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}Factuur maken
        </Button>
      </div>
    </div>
  );
};

export const QuoteInvoicesCard = ({ quoteId, title = 'Facturatie', onChange, highlight = false }) => {
  const [summary, setSummary] = useState(null);
  const fetchSummary = useCallback(async () => {
    try { const res = await axios.get(`${API}/quotes/${quoteId}/invoices`, { headers: auth() }); setSummary(res.data); }
    catch { setSummary(null); }
  }, [quoteId]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  const load = () => { fetchSummary(); if (onChange) onChange(); };
  if (!summary) return null;
  const pct = Math.min(100, summary.invoiced_percentage);
  return (
    <Card data-testid={`quote-invoices-card-${quoteId}`} id={`quote-invoicing-${quoteId}`} className={`scroll-mt-28 ${highlight ? 'ring-2 ring-[#500000] transition-shadow' : ''}`}>
      <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="flex flex-wrap justify-between gap-2 text-sm mb-2">
            <span data-testid="quote-invoiced-summary">Gefactureerd: <strong>{pc(summary.invoiced_percentage)}</strong> · {eur(summary.invoiced_amount)}</span>
            <span data-testid="quote-remaining-summary">Nog te factureren: <strong>{pc(summary.remaining_percentage)}</strong> · {eur(summary.remaining_amount)}</span>
          </div>
          <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
            <div className="h-full transition-[width] duration-500" style={{ width: `${pct}%`, backgroundColor: BRAND }} />
          </div>
          <p className="text-xs mt-1" style={{ color: '#6B7280' }}>Offertetotaal {eur(summary.quote_total_incl_vat)} incl. BTW</p>
        </div>
        <NewInvoiceForm quoteId={quoteId} summary={summary} onCreated={load} />
        {summary.invoices.length > 0 && (
          <div data-testid="quote-invoices-list">{summary.invoices.map((inv) => <InvoiceRow key={inv.id} inv={inv} onChanged={load} />)}</div>
        )}
      </CardContent>
    </Card>
  );
};
